import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { LogoService } from '@src/app/core/services/logo/logo.service';
import { formatSize } from '@src/app/core/utils/file.utils';
import { ImportsModule } from '@src/app/imports';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { ProgressBarModule } from 'primeng/progressbar';
import { BaseDialogComponent } from '../base-dialog/base-dialog.component';
import { LogoFileItemComponent } from './components/logo-file-item/logo-file-item.component';
import { finalize } from 'rxjs';
import { Store } from '@ngrx/store';
import { AuthSelectors } from '@src/app/store/auth/auth.selectors';
import { AuthActions } from '@src/app/store/auth/auth.actions';

export enum AddEditLogoDialogMode {
  ADD = 'ADD',
  EDIT = 'EDIT',
}

@Component({
  selector: 'app-add-edit-logo-dialog',
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    TranslateModule,
    FileUploadModule,
    ProgressBarModule,
    ImportsModule,
    LogoFileItemComponent,
  ],
  templateUrl: './add-edit-logo.dialog.component.html',
  styleUrl: './add-edit-logo.dialog.component.scss',
})
export class AddEditLogoDialogComponent extends BaseDialogComponent {
  store = inject(Store);
  selectedCompany = this.store.selectSignal(
    AuthSelectors.selectSelectedCompany,
  );
  fileUpload = viewChild<FileUpload>('fileUpload');
  dialogMode = input<AddEditLogoDialogMode>(AddEditLogoDialogMode.ADD);
  existingFile = input<{
    objectURL: string | null;
    name?: string;
    size: number;
  } | null>(null);
  loading = signal<boolean>(false);
  uploadError = signal<string | null>(null);
  isAdd = computed(() => this.dialogMode() === AddEditLogoDialogMode.ADD);
  formatSize = formatSize;

  constructor(private logoService: LogoService) {
    super();
  }

  override close() {
    super.close();
    setTimeout(() => {
      this.fileUpload()?.clear();
      this.loading.set(false);
      this.uploadError.set(null);
    }, 200);
  }

  getDialogHeader(): string {
    return `header.logo.${this.isAdd() ? 'addHeader' : 'editHeader'}`;
  }

  saveLogo() {
    // Clear any previous errors
    this.uploadError.set(null);

    if (!this.fileUpload()?.files?.length) {
      this.uploadError.set('fileupload.noFileSelected');
      return;
    }

    const file = this.fileUpload()?.files[0];
    const companyId = this.selectedCompany()?.CLIEN_NM;

    if (!file) {
      this.uploadError.set('fileupload.invalidFileType');
      return;
    }

    if (!companyId) {
      this.uploadError.set(
        'header.logo.companyMissing',
      );
      return;
    }

    this.uploadLogo(file);
  }

  uploadLogo(file: File) {
    this.loading.set(true);
    this.uploadError.set(null);

    const companyId = this.selectedCompany()?.CLIEN_NM;
    const currentCompany = this.selectedCompany();

    this.logoService
      .addOrUpdateLogo(file, companyId)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          // Reload company logos to update the store
          this.store.dispatch(AuthActions.loadCompanyLogos());
          this.close();
        },
        error: (error) => {
          console.error('❌ Error uploading/updating logo:', error);

          // Provide more specific error messages
          let errorMessage = 'header.logo.uploadingError';

          if (error.status === 403) {
            errorMessage =
              'header.logo.accessDenied';
          } else if (error.status === 401) {
            errorMessage = 'common.errorMessages.sessionExpired';
          } else if (error.status === 413) {
            errorMessage = 'fileupload.invalidFileSizeMessageSummary.';
          } else if (error.status === 415) {
            errorMessage =
              'fileupload.invalidFileType';
          } else if (
            error.message &&
            error.message.includes('Company ID is required')
          ) {
            errorMessage =
              'header.logo.companyMissing';
          }

          this.uploadError.set(errorMessage);
        },
      });
  }
}
